import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { useGetDrivers, useSearchDrivers } from "@web-app/api";
import { DutyStatus, User, UserSearch } from "@web-app/types";

import Avatar from "../Avatar";
import { Icon } from "../Icon";
import type { IconsType } from "../Icon/utils";

type AssigneeSelectType = {
  className?: string;
  disabled?: boolean;
  dropdownClassName?: string;
  iconName?: IconsType;
  onChange?: (assigneeId: string | null) => void;
  placeholder?: string;
  required?: boolean;
  selectedId?: string | null;
  size?: "md" | "sm";
};

export default function SelectAssignee(props: AssigneeSelectType) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState<User | null>(null);
  const initializedRef = useRef(false);
  const lastSelectedIdRef = useRef<string | null>(null);

  const {
    data: assigneesList,
    isSuccess,
    isLoading: isSearchLoading,
  } = useSearchDrivers(debouncedSearchTerm);

  // Fetch driver data when we have a selectedId but no selectedAssignee yet
  // Keep the query stable until we have the data we need
  const needsDriverFetch =
    !!props.selectedId && selectedAssignee?.id !== props.selectedId;
  const { data: selectedDriverData, isLoading: isSelectedDriverLoading } =
    useGetDrivers(
      needsDriverFetch && props.selectedId ? [props.selectedId] : [],
    );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const currentSelectedId = selectedAssignee?.id || null;
    const isExternalChange = lastSelectedIdRef.current !== props.selectedId;
    const needsInitialData =
      props.selectedId &&
      props.selectedId !== currentSelectedId &&
      selectedDriverData?.length;

    // Should update if:
    // 1. Not initialized yet, OR
    // 2. External change with different selected ID, OR
    // 3. We have selectedId but no selectedAssignee and driver data just arrived
    const shouldUpdate =
      !initializedRef.current ||
      (isExternalChange && props.selectedId !== currentSelectedId) ||
      needsInitialData;

    if (shouldUpdate) {
      if (props.selectedId) {
        let assignee = assigneesList?.find((a) => a.id === props.selectedId);

        if (!assignee) {
          assignee = selectedDriverData?.find((a) => a.id === props.selectedId);
        }

        if (assignee) {
          const normalizedDriver: User = {
            ...assignee,
            driverDetails: assignee.driverProfile,
          };
          setSelectedAssignee(normalizedDriver);
          lastSelectedIdRef.current = props.selectedId || null;
        }
      } else if (!props.selectedId && currentSelectedId !== null) {
        setSelectedAssignee(null);
        lastSelectedIdRef.current = props.selectedId || null;
      }

      initializedRef.current = true;
    }
  }, [
    props.selectedId,
    selectedDriverData,
    assigneesList,
    selectedAssignee?.id,
  ]);

  useEffect(() => {
    if (
      props.selectedId &&
      selectedAssignee?.id !== props.selectedId &&
      assigneesList
    ) {
      const assignee = assigneesList.find((a) => a.id === props.selectedId);
      if (assignee) {
        const normalizedDriver: User = {
          ...assignee,
          driverDetails: assignee.driverProfile,
        };
        setSelectedAssignee(normalizedDriver);
      }
    }
  }, [assigneesList, props.selectedId, selectedAssignee?.id]);

  const isLoading =
    (!isSuccess && debouncedSearchTerm === "") || isSelectedDriverLoading;
  const isSearching = isSearchLoading && debouncedSearchTerm !== "";

  const dropdownDrivers = assigneesList || [];

  const selectedAssigneeOption = selectedAssignee
    ? {
        dutyStatus: selectedAssignee.driverDetails.dutyStatus,
        imageUrl: selectedAssignee.image,
        label: selectedAssignee.name,
        phone: selectedAssignee.phone,
        value: selectedAssignee.id,
      }
    : null;

  const handleOnChange = (assignee: UserSearch | null) => {
    if (assignee && assignee.id) {
      const normalizedDriver: User = {
        ...assignee,
        driverDetails: assignee.driverProfile,
      };

      setSelectedAssignee(normalizedDriver);
      lastSelectedIdRef.current = assignee.id;
      props.onChange?.(assignee.id);
      return;
    }

    setSelectedAssignee(null);
    lastSelectedIdRef.current = null;
    props.onChange?.(null);
  };

  return (
    <Combobox
      value={selectedAssigneeOption}
      immediate
      onChange={(
        value: {
          dutyStatus: DutyStatus;
          imageUrl: string | null;
          label: string;
          phone: string;
          value: string;
        } | null,
      ) => {
        if (value && assigneesList) {
          const assignee = assigneesList.find((a) => a.id === value.value);
          handleOnChange(assignee || null);
        } else {
          handleOnChange(null);
        }
      }}
      disabled={props.disabled}
    >
      {({ open }) => (
        <div className="relative w-full">
          <div className="relative leading-none">
            <div className="relative">
              {props.iconName && (
                <Icon
                  name={props.iconName}
                  className={clsx(
                    "absolute top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none",
                    props.size === "sm"
                      ? "left-2.5 h-3.5 w-3.5"
                      : "left-3 h-4 w-4",
                  )}
                />
              )}
              <ComboboxInput
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(event.target.value)
                }
                className={clsx(
                  "block w-full rounded-md border text-gray-900",
                  props.size === "sm"
                    ? "px-2 py-1 text-xs"
                    : "px-3 py-2 text-xs lg:text-sm",
                  "border-gray-300 focus:ring-gray-500",
                  "focus:outline-none focus:ring-2",
                  "disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500",
                  "transition-all duration-200 ease-in-out",
                  // Left padding for leading icon
                  props.iconName && (props.size === "sm" ? "pl-8" : "pl-9"),
                  (isLoading || isSearching) && "pr-8", // Add right padding for spinner
                  props.className,
                )}
                displayValue={(assignee: typeof selectedAssigneeOption) =>
                  assignee?.label ?? ""
                }
                placeholder={
                  props.disabled
                    ? "Unassigned"
                    : (props.placeholder ?? "Assign a Driver")
                }
              />
              {(isLoading || isSearching) && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Icon
                    name="spinner"
                    className="h-3  lg:h-4 w-3 lg:w-4 animate-spin text-gray-400"
                  />
                </div>
              )}
            </div>
            {selectedAssigneeOption &&
              !props.disabled &&
              !(isLoading || isSearching) && (
                <span className="-transition-y-1/2 absolute right-0 top-1/2 flex">
                  {" "}
                  <Icon
                    name="xMark"
                    className="h-3 w-3 -translate-y-1/2 cursor-pointer pl-2 pr-2 text-gray-500 transition ease-in"
                    onClick={() => {
                      setSelectedAssignee(null);
                      lastSelectedIdRef.current = null;
                      props.onChange?.(null);
                    }}
                  />
                </span>
              )}
          </div>
          <Transition
            show={open}
            as="div"
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className="relative z-[60] w-full"
          >
            <ComboboxOptions
              className={clsx(
                "absolute mt-1 max-h-56 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs lg:text-sm",
                props.dropdownClassName || "w-full",
              )}
              static
            >
              {dropdownDrivers?.map?.((assignee) => (
                <ComboboxOption
                  key={assignee.id}
                  value={{
                    dutyStatus: assignee.driverProfile.dutyStatus,
                    imageUrl: assignee.image,
                    label: assignee.name,
                    phone: assignee.phone,
                    value: assignee.id,
                  }}
                  className={({ active }) =>
                    clsx(
                      active ? "bg-black text-white" : "text-gray-900",
                      "relative cursor-pointer select-none py-2 pl-3 pr-9 flex items-center gap-2",
                    )
                  }
                >
                  <Avatar
                    size={8}
                    image={assignee.image || ""}
                    name={assignee.name}
                    dutyStatus={assignee.driverProfile.dutyStatus}
                  />
                  {assignee.name}{" "}
                  <span className="text-xs text-gray-500">
                    {assignee.phone}
                  </span>
                </ComboboxOption>
              ))}
              <div className="border-t border-gray-200 mt-1 pt-3 pb-2">
                <div className="flex flex-col items-center justify-center py-2 px-4 text-center">
                  <p className="text-xs text-gray-500 flex items-center">
                    <Icon name="circleExclamation" className="mr-1 h-3 w-3" />
                    Can't find an assignee? Try searching by name or phone.
                  </p>
                </div>
              </div>
            </ComboboxOptions>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}
